/**
 * Recharge Subscription Data Helper
 * Utility functions to extract subscription configuration data from Shopify pages
 */

const rechargeSubDebugEnabled =
  typeof window !== 'undefined' &&
  (window.__MITOQ_SUB_DEBUG__ === true ||
    new URLSearchParams(window.location.search).get('mitoqSubDebug') === '1')

const rechargePerfLog = (message, meta = null) => {
  if (!rechargeSubDebugEnabled) return

  if (meta) {
    console.log(`[RechargeData][perf] ${message}`, meta)
    return
  }

  console.log(`[RechargeData][perf] ${message}`)
}

class RechargeDataExtractor {
  /**
   * Extract SubscriptionWidgetConfig from script contents
   * @param {string[]} scriptContents - Array of script content strings
   * @returns {Object} Complete SubscriptionWidgetConfig object
   */
  static extractConfig(scriptContents) {
    const extractStart = performance.now()

    try {
      const mockWindow = { SubscriptionWidgetConfig: {} }

      scriptContents.forEach((content) => {
        if (content && content.trim()) {
          /* eslint-disable no-new-func */
          new Function('window', content)(mockWindow)
          /* eslint-enable no-new-func */
        }
      })

      rechargePerfLog('extractConfig success', {
        durationMs: Number((performance.now() - extractStart).toFixed(1)),
      })

      return mockWindow.SubscriptionWidgetConfig
    } catch (err) {
      rechargePerfLog('extractConfig failed', {
        durationMs: Number((performance.now() - extractStart).toFixed(1)),
      })
      console.error('Failed to extract config from scripts:', err)
      return null
    }
  }

  /**
   * Parse HTML content to extract script tags
   * @param {string} htmlContent - HTML content to parse
   * @returns {Object} Object containing script contents
   */
  static parseScriptsFromHTML(htmlContent) {
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')

    const settingsScript = doc.querySelector(
      '#SubscriptionWidgetConfig__shopify_settings'
    )
    const configsScript = doc.querySelector(
      '#SubscriptionWidgetConfig__configs'
    )

    return {
      settingsContent: settingsScript ? settingsScript.textContent : null,
      configsContent: configsScript ? configsScript.textContent : null,
      hasSettings: !!settingsScript,
      hasConfigs: !!configsScript,
    }
  }

  /**
   * Fetch and extract subscription data from current page
   * @returns {Promise<Object|null>} Subscription data object
   */
  static async fetchFromCurrentPage() {
    const start = performance.now()

    try {
      const settingsScript = document.querySelector(
        '#SubscriptionWidgetConfig__shopify_settings'
      )
      const configsScript = document.querySelector(
        '#SubscriptionWidgetConfig__configs'
      )

      // In some environments (e.g. stores or pages without Recharge enabled)
      // these scripts simply won't exist. Treat that as "no subscription data"
      // instead of an error so the rest of the theme can degrade gracefully.
      if (!settingsScript || !configsScript) {
        // Keep this as an info-level message to avoid noisy error reporting
        // tools in environments where Recharge is intentionally absent.
        console.info(
          'Recharge subscription scripts not found on current page; skipping subscription pricing.'
        )
        rechargePerfLog('fetchFromCurrentPage scripts missing', {
          durationMs: Number((performance.now() - start).toFixed(1)),
        })
        return null
      }

      const subscriptionConfig = this.extractConfig([
        settingsScript.textContent,
        configsScript.textContent,
      ])

      if (!subscriptionConfig) {
        throw new Error('Failed to extract subscription configuration')
      }

      const {
        shop: shopifyConfig,
        formattedPrices,
        configs,
      } = subscriptionConfig

      return {
        shopifyConfig,
        formattedPrices,
        configs,
        source: 'current-page',
      }
    } catch (err) {
      rechargePerfLog('fetchFromCurrentPage failed', {
        durationMs: Number((performance.now() - start).toFixed(1)),
      })
      console.error('Failed to fetch subscription data from current page:', err)
      return null
    }
  }

  /**
   * Fetch and extract subscription data from external URL
   * @param {string} url - URL to fetch data from
   * @returns {Promise<Object|null>} Subscription data object
   */
  static async fetchFromExternalUrl(url) {
    const start = performance.now()

    try {
      rechargePerfLog('fetchFromExternalUrl start', { url })
      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const htmlContent = await response.text()
      rechargePerfLog('fetchFromExternalUrl html received', {
        durationMs: Number((performance.now() - start).toFixed(1)),
      })

      const scripts = this.parseScriptsFromHTML(htmlContent)

      if (!scripts.hasSettings)
        throw new Error('No Recharge settings script found in external page')
      if (!scripts.hasConfigs)
        throw new Error('No Recharge configs script found in external page')

      const subscriptionConfig = this.extractConfig([
        scripts.settingsContent,
        scripts.configsContent,
      ])

      if (!subscriptionConfig) {
        throw new Error(
          'Failed to extract subscription configuration from external page'
        )
      }

      const {
        shop: shopifyConfig,
        formattedPrices,
        configs,
      } = subscriptionConfig

      return {
        shopifyConfig,
        formattedPrices,
        configs,
        source: 'external-url',
        sourceUrl: url,
      }
    } catch (err) {
      rechargePerfLog('fetchFromExternalUrl failed', {
        url,
        durationMs: Number((performance.now() - start).toFixed(1)),
      })
      console.error('Failed to fetch subscription data from external URL:', err)
      return null
    }
  }

  /**
   * Universal method to fetch subscription data
   * @param {string|null} url - Optional URL to fetch from. If null, fetches from current page
   * @returns {Promise<Object|null>} Subscription data object
   */
  static async fetchSubscriptionData(url = null) {
    if (url) return this.fetchFromExternalUrl(url)
    return this.fetchFromCurrentPage()
  }
}

/**
 * Convenience function to get Recharge subscription data
 * @param {string|null} url - Optional URL to fetch from. If null, fetches from current page
 * @returns {Promise<Object|null>} Object containing shopifyConfig, formattedPrices, and configs
 */
export async function getProductRechargeData(url = null) {
  return RechargeDataExtractor.fetchSubscriptionData(url)
}

/**
 * Get only shopify configuration
 * @param {string|null} url - Optional URL to fetch from
 * @returns {Promise<Object|null>} Shopify configuration object
 */
export async function getShopifyConfig(url = null) {
  const data = await RechargeDataExtractor.fetchSubscriptionData(url)
  return data ? data.shopifyConfig : null
}

/**
 * Get only formatted prices
 * @param {string|null} url - Optional URL to fetch from
 * @returns {Promise<Object|null>} Formatted prices object
 */
export async function getFormattedPrices(url = null) {
  const data = await RechargeDataExtractor.fetchSubscriptionData(url)
  return data ? data.formattedPrices : null
}

/**
 * Get only configs
 * @param {string|null} url - Optional URL to fetch from
 * @returns {Promise<Object|null>} Configs object
 */
export async function getConfigs(url = null) {
  const data = await RechargeDataExtractor.fetchSubscriptionData(url)
  return data ? data.configs : null
}

// Export the main class as well for advanced usage
export { RechargeDataExtractor }
